

import { useAppContext } from './context/AppContext';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ClientForm from './components/ClientForm';
import GlobalConfigPanel from './components/GlobalConfigPanel';
import SpecsSection from './components/SpecsSection';
import CatalogModal from './components/CatalogModal';
import ProposalsModal from './components/ProposalsModal';
import PwaBanners from './components/PwaBanners';
import ProjectImagesCard from './components/ProjectImagesCard';
import PricingTable from './components/PricingTable';
import CostingSummary from './components/CostingSummary';
import ProposalPreview from './components/ProposalPreview';
import Login from './components/Login';
import WelcomePage from './components/WelcomePage';
import UserManagement from './components/UserManagement';
import SalesPointManagement from './components/SalesPointManagement';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import { Loader2, Lock, X } from 'lucide-react';
import { XmlUnmatchedModal } from './components/XmlUnmatchedModal';

export default function App() {
  const {
    mode,
    proposalType,
    fileInputRef, jsonInputRef, projectImageInputRef,
    handleImportDraft,
    handleFileChange, handleProjectImagesUpload,
    currentUser,
    userRole,
    loadingAuth,
    readOnlyState,
    exitReadOnlyMode,
    pendingXmlImport,
    handleConfirmXmlImport,
  } = useAppContext();

  if (loadingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8F7]">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#945f46] mx-auto" />
          <p className="text-xs text-stone-400 font-semibold uppercase tracking-wider">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Login />;
  }





  return (
    <div className="min-h-screen flex font-sans">
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0">
      <PwaBanners />

      {/* Hidden File Picker */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />

      {/* Hidden Project Images Picker */}
      <input
        type="file"
        ref={projectImageInputRef}
        onChange={handleProjectImagesUpload}
        accept="image/*"
        multiple
        className="hidden"
      />

      {/* Hidden JSON File Picker */}
      <input
        type="file"
        ref={jsonInputRef}
        onChange={handleImportDraft}
        accept=".json"
        className="hidden"
      />


      {/* Header */}
      <Header />

      {/* READ ONLY PROPOSAL BANNER */}
      {readOnlyState.isReadOnly && (
        <div className="bg-amber-500 text-stone-950 px-6 py-2.5 flex items-center justify-between text-xs font-bold shadow-sm border-b border-amber-600 animate-in fade-in no-print">
          <div className="flex items-center space-x-2">
            <Lock className="w-4 h-4 text-stone-950 shrink-0" />
            <span>
              SALT OKUNUR İNCELEME MODU — Bu teklif başka bir tasarımcıya aittir ({readOnlyState.designerName || 'Tasarımcı'}). Üzerinde değişiklik yapılamaz ve buluta kaydedilemez.
            </span>
          </div>
          <button
            type="button"
            onClick={exitReadOnlyMode}
            className="px-3 py-1 bg-stone-900 hover:bg-stone-800 text-amber-400 rounded-lg font-extrabold text-[11px] transition-colors shrink-0 flex items-center space-x-1"
          >
            <X className="w-3.5 h-3.5" />
            <span>İncelemeden Çık</span>
          </button>
        </div>
      )}


      {/* Main Container */}
      <main className="flex-1 p-6 md:p-8 max-w-7xl mx-auto w-full print-container">
        
        {/* WELCOME MODE SCREEN */}
        {mode === 'welcome' && <WelcomePage />}

        {/* EDIT MODE SCREEN */}
        {mode === 'edit' && (
          <div className="no-print grid grid-cols-1 lg:grid-cols-12 gap-8">

            {proposalType === 'bulk' ? (
              <>
                {/* Left column: Client Info, then Mutfak Konfigürasyonu, Proje Görselleri — stacked with the page's standard gap, independent of the right column's height. Daire tipi list now lives inside Proje Geneli Özet (Row 1 right). */}
                <div className="lg:col-span-4 space-y-8 self-start">
                  <ClientForm />
                  <GlobalConfigPanel />
                  <ProjectImagesCard />
                </div>

                {/* Right column: Proje Geneli Özet, then Teklif Ürün Detayları */}
                <div className="lg:col-span-8 space-y-8 self-start">
                  <CostingSummary />
                  <SpecsSection />
                </div>
              </>
            ) : (
              <>
                {/* Row 1, Left: Client Info Card (equal height with Pricing Table) */}
                <div className="lg:col-span-4">
                  <ClientForm />
                </div>

                {/* Row 1, Right: Flat Quoting Table (equal height with Client Info) */}
                <div className="lg:col-span-8">
                  <PricingTable />
                </div>

                {/* Row 2, Left: Config & Project Images */}
                <div className="lg:col-span-4 space-y-6 self-start">
                  <GlobalConfigPanel />
                  <ProjectImagesCard />
                </div>

                {/* Row 2, Right: Visual Specification List & Costing Summary */}
                <div className="lg:col-span-8 space-y-6 self-start">
                  <SpecsSection />
                  <CostingSummary />
                </div>
              </>
            )}

          </div>
        )}

        {/* USERS MODE SCREEN (ADMIN ONLY) */}
        {mode === 'users' && userRole === 'admin' && (
          <UserManagement />
        )}

        {/* SALES POINTS MODE SCREEN (ADMIN ONLY) */}
        {mode === 'salesPoints' && userRole === 'admin' && (
          <SalesPointManagement />
        )}

        {/* ANALYTICS MODE SCREEN (ADMIN ONLY) */}
        {mode === 'analytics' && userRole === 'admin' && (
          <AnalyticsDashboard />
        )}



        <ProposalPreview />

      </main>

      {/* Footer */}
      <footer className="no-print bg-[#18181B] text-stone-500 text-xs py-4 text-center border-t border-stone-800/80 mt-12">
        <p>© 2026 Eczacıbaşı İntema Mutfak. Tüm Hakları Saklıdır.</p>
      </footer>
      </div>

      {/* CATALOG MANAGEMENT MODAL */}
      <CatalogModal />

      {/* PROPOSALS ARCHIVE MODAL */}
      <ProposalsModal />

      {/* XML UNMATCHED ACCESSORIES MODAL */}
      <XmlUnmatchedModal
        isOpen={!!pendingXmlImport}
        unmatchedAccessories={pendingXmlImport?.parsed.unmatchedAccessories || []}
        onConfirm={handleConfirmXmlImport}
      />

    </div>
  );
}
